/**
 * Generates CSS for the preview mode survey layout.
 * By default the survey box is positioned inside the preview pane according
 * to the configured position settings (mirroring the onsite layout, but
 * anchored to the :host pane instead of the viewport).
 *
 * @param {object} style - The survey style configuration object.
 * @param {object} [options] - Layout options.
 * @param {boolean} [options.staticLayout] - Render the survey box in normal
 *   flow (no position handling); used by the question preview tab where the
 *   surrounding component controls placement.
 * @param {string} [options.previewType] - Survey type the preview mimics;
 *   'embed' centers the box in the pane regardless of position settings.
 * @returns {string} CSS string for preview layout.
 */
export function layoutPreviewCSS(style, options = {}) {
    const staticLayout = options.staticLayout === true;

    // Embed surveys support position settings (left, middle/center, right)
    const verticalPosition = options.previewType === 'embed' ? 'middle' : style.verticalPosition;
    let horizontalPosition = options.previewType === 'embed' ? (style.position || 'middle') : style.position;
    if (horizontalPosition === 'center') horizontalPosition = 'middle';

    const widthValue = style.widthValue ?? 390;
    const widthUnit = style.widthUnit ?? 'px';
    const heightValue = style.heightUnit === 'auto' ? 'auto' : style.heightValue ?? 'auto';
    const heightUnit = style.heightUnit === 'auto' ? '' :  style.heightUnit ?? '';
    const boxShadow = style.hasBoxShadow ? '0 0 20px rgba(0, 0, 0, 0.24)' : 'none';

    /* Same position calculation as the onsite layout, but with absolute
       positioning so the box anchors to the preview pane (:host) */
    const topVal = verticalPosition === 'top'
        ? '32px'
        : verticalPosition === 'middle'
            ? '50%'
            : 'auto';

    const bottomVal = verticalPosition === 'bottom' ? '32px' : 'auto';

    const leftVal = horizontalPosition === 'left'
        ? '32px'
        : horizontalPosition === 'middle'
            ? '50%'
            : 'auto';

    const rightVal = horizontalPosition === 'right' ? '32px' : 'auto';

    const transformVal = verticalPosition === 'middle' && horizontalPosition === 'middle'
        ? 'translate(-50%, -50%)'
        : verticalPosition === 'middle'
            ? 'translateY(-50%)'
            : horizontalPosition === 'middle'
                ? 'translateX(-50%)'
                : 'none';

    const positionCSS = staticLayout ? '' : `
    :host {
        /* Fill the pane the preview host spans (e.g. the builder wrapper),
           so box and bubble anchor to the pane edges */
        height: 100%;
        /* Keeps the absolutely positioned bubble (108px + 32px inset + tail)
           visible even when the survey wrapper is hidden and the pane
           has no fixed height */
        min-height: 148px;
    }

    .ph-survey {
        position: absolute;
        top: ${topVal};
        bottom: ${bottomVal};
        left: ${leftVal};
        right: ${rightVal};
        transform: ${transformVal};
    }
    `;

    return `
    /* The preview host is the positioning context for the survey box
       and the minimized bubble */
    :host {
        position: relative;
        display: block;
    }
    ${positionCSS}
    /* ===== Preview survey box ===== */
    .ph-survey {
        width: ${widthValue}${widthUnit};
        height: ${heightValue}${heightUnit};
        display: flex;
        flex-direction: column;
        --ph-options-max-height: ${style.heightUnit === 'auto' ? '370px' : '100%'};
        background-color: var(--ph-bg-color);
        padding: 16px;
        border-radius: 8px;
        font-family: var(--ph-font-family);
        box-shadow: ${boxShadow};
        line-height: normal;
        box-sizing: border-box;
        color: var(--ph-text-color);
    }`;
}
